import { useState, useEffect } from "react";
import "../Register/register.css";
import { Container, Row, Col } from "react-bootstrap";
import fieldCover from "../../assets/covers/field.png";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, resetRegister } from "../../redux/registerSlice";
// import { useNavigate } from "react-router-dom";
// Import Material UI components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
// Import Toastify
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
// Import icons for password visibility
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const Register = ({ setIsRegister }) => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  
  // Get registration state from Redux store
  const { loading, isRegistered, error } = useSelector((state) => state.register);
  
  // State for backdrop loader
  const [showLoader, setShowLoader] = useState(false);
  // State for transition loader
  const [isTransitioning, setIsTransitioning] = useState(false);
  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    companyName: "",
    role: "user",
  });

  const [formErrors, setFormErrors] = useState({});

  // Handle transition to login page
  const handleTransitionToLogin = () => {
    setIsTransitioning(true);
    // Simulate a brief loading time for a natural transition
    setTimeout(() => {
      setIsRegister(false);
      setIsTransitioning(false);
    }, 500); // 500ms delay for a smooth transition
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Clear the error when user starts typing
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: "",
      });
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));
    // Clear phone error if it exists
    if (formErrors.phone) {
      setFormErrors({
        ...formErrors,
        phone: "",
      });
    }
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) errors.firstName = "First name is required";
    if (!formData.lastName.trim()) errors.lastName = "Last name is required";
    if (!formData.email.trim()) errors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) errors.email = "Email is invalid";
    
    if (!formData.password) errors.password = "Password is required";
    else if (formData.password.length < 6) errors.password = "Password must be at least 6 characters";
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    
    if (!formData.phone) errors.phone = "Phone number is required";
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      // Show toast for validation errors
      toast.error("Please fill all the fields.");
      return;
    }
    
    // Show loader
    setShowLoader(true);
    
    // Prepare data for API
    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      phone: formData.phone,
      companyName: formData.companyName,
    };
    
    try {
      // Dispatch the register action
      const result = await dispatch(registerUser(userData));
      
      // Check if the action was fulfilled
      if (result.meta.requestStatus === 'fulfilled') {
        // const responseData = result.payload;
      }
    } catch (err) {
      console.error("Registration error:", err);
      setShowLoader(false); // Hide loader on error
      toast.error("Registration failed. Please try again later.");
    }
  };

  // Watch for successful registration
  useEffect(() => {
    if (isRegistered) {
      // Hide loader
      setShowLoader(false);
      
      // Reset the form after successful registration
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        phone: "",
        companyName: "",
      });
      
      // Show success toast
      toast.success("Registration successful!");
      
      // Redirect to login or dashboard
      setIsRegister(false);
      
      // Reset the registration state
      dispatch(resetRegister());
    }
  }, [isRegistered, dispatch, setIsRegister]);
  
  // Watch for registration errors
  useEffect(() => {
    if (error) {
      setShowLoader(false);
      toast.error(error.message || "Registration failed. Please try again.");
    }
  }, [error]);
  
  // Hide loader when loading state changes to false
  useEffect(() => {
    if (!loading && showLoader) {
      setShowLoader(false);
    }
  }, [loading]);
  
  // Ensure loader is hidden when component unmounts
  useEffect(() => {
    return () => {
      setShowLoader(false);
      setIsTransitioning(false);
    };
  }, []);

  return (
    <>
      {/* Material UI Backdrop Loader */}
      <Backdrop
          sx={{ 
            color: '#37b000', 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.2)' // Slightly transparent black
          }}
          open={showLoader}
        >
        <CircularProgress color="37b000" />
      </Backdrop>
      
      {/* Transition Loader */}
      <Backdrop
          sx={{ 
            color: '#37b000', 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(0, 0, 0, 0.2)' // Slightly transparent black
          }}
          open={isTransitioning}
        >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Container fluid className="register-page-container">
        <Row className="register-row">
          <Col lg={7} className="register-left-column">
            <div className="register-form-content">
              <div className="register-branding">
                <h1 className="goplay-logo">GoPlay</h1>
                <p className="portal-title">Goplay Sports Facility Management Portal</p>
              </div>
              <div className="nav-tabs">
                <span 
                  className="nav-tab"
                  onClick={handleTransitionToLogin}
                >
                  Login
                </span>
                <span 
                  className="nav-tab active"
                >
                  Register
                </span>
              </div>
              <form className="register-form-wrapper" onSubmit={handleSubmit}>
                <Row>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">First Name*</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="First Name"
                        className={`register-input ${formErrors.firstName ? "input-error" : ""}`}
                      />
                      {formErrors.firstName && <div className="error-message">{formErrors.firstName}</div>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">Last Name*</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Last Name"
                        className={`register-input ${formErrors.lastName ? "input-error" : ""}`}
                      />
                      {formErrors.lastName && <div className="error-message">{formErrors.lastName}</div>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">Email*</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email"
                        className={`register-input ${formErrors.email ? "input-error" : ""}`}
                      />
                      {formErrors.email && <div className="error-message">{formErrors.email}</div>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">Phone Number*</label>
                      <PhoneInput
                        country={"pk"}
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder="Enter phone number"
                        inputClass={`form-control reg ${formErrors.phone ? "input-error" : ""}`}
                      />
                      {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">Password*</label>
                      <div className="password-input-container">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Password"
                          className={`register-input ${formErrors.password ? "input-error" : ""}`}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {formErrors.password && <div className="error-message">{formErrors.password}</div>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form-group">
                      <label className="form-label">Confirm Password*</label>
                      <div className="password-input-container">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Confirm Password"
                          className={`register-input ${formErrors.confirmPassword ? "input-error" : ""}`}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {formErrors.confirmPassword && <div className="error-message">{formErrors.confirmPassword}</div>}
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="form-group">
                      <label className="form-label">Company Name</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder="Company Name"
                        className="register-input"
                      />
                    </div>
                  </Col>
                  <Col lg={12}>
                    <button type="submit" className="register-button" disabled={loading}>
                      {loading ? "Registering..." : "Register"}
                    </button>
                    <p className="switch-link">
                      Already have an account?{" "}
                      <span onClick={handleTransitionToLogin} className="link-text">
                        Login
                      </span>
                    </p>
                  </Col>
                </Row>
              </form>
              <p className="copyright-text">© GoPlay — All rights reserved.</p>
            </div>
          </Col>
          <Col lg={5} className="cover-column">
            <div className="cover-image-container">
              <img src={fieldCover} alt="Sports field" className="cover-image" />
            </div>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default Register;